<template>
  <teleport to="body" v-if="show">
    <div class="modal-overlay" @click.self="closeModal">
      <div class="modal-container">
        <div class="modal-header">
          <div class="modal-header-content">
            <i class="mdi mdi-account-multiple-plus"></i>
            <h3>{{ title }}</h3>
          </div>
          <button class="modal-close" @click="closeModal">
            <i class="mdi mdi-close"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Поле пошуку -->
          <div class="search-input-wrapper">
            <i class="mdi mdi-magnify"></i>
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="Пошук по імені або посаді..."
              @input="filterWorkers"
            />
            <button
              v-if="searchQuery"
              class="clear-search-btn"
              @click="
                searchQuery = '';
                filterWorkers();
              "
              title="Очистити пошук"
            >
              <i class="mdi mdi-close"></i>
            </button>
          </div>

          <!-- Список працівників -->
          <div v-if="filteredWorkersList.length > 0" class="workers-list">
            <div
              v-for="worker in filteredWorkersList"
              :key="worker.workerId"
              :class="[
                'worker-item',
                { selected: isWorkerSelected(worker.workerId) },
                { disabled: isWorkerAlreadyAdded(worker.workerId) },
              ]"
              @click="toggleWorkerSelection(worker)"
            >
              <div class="worker-avatar">
                <img
                  v-if="worker.avatar"
                  :src="worker.avatar"
                  :alt="worker.workerName"
                  class="avatar-img"
                />
                <i v-else class="mdi mdi-account"></i>
              </div>

              <div class="worker-info">
                <div class="worker-name">
                  {{ worker.workerName }}
                  <span
                    v-if="isWorkerAlreadyAdded(worker.workerId)"
                    class="already-added-badge"
                  >
                    Вже доданий
                  </span>
                </div>
                <div class="worker-details">
                  <span class="worker-job">
                    {{ mutateWorkerJob(worker.workerJob) }}
                  </span>
                  <!-- <span class="worker-status" :class="getStatusClass(worker)">
                    <i class="mdi mdi-circle"></i>
                    {{ worker.statusShift === "open" ? "На зміні" : "Не на зміні" }}
                  </span> -->
                </div>
              </div>

              <div class="worker-actions">
                <i
                  v-if="isWorkerSelected(worker.workerId)"
                  class="mdi mdi-check-circle worker-selected-icon"
                ></i>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <i class="mdi mdi-account-search"></i>
            <p>Працівників не знайдено</p>
          </div>

          <!-- Вибрані працівники -->
          <div
            v-if="selectedWorkers.length > 0"
            class="selected-workers-section"
          >
            <div class="selected-header">
              <span>Вибрано: {{ selectedWorkers.length }}</span>
              <button class="clear-all-btn" @click="clearAllSelections">
                <i class="mdi mdi-close"></i>
                Очистити все
              </button>
            </div>
            <div class="selected-workers-list">
              <div
                v-for="worker in selectedWorkers"
                :key="worker.workerId"
                class="selected-worker-chip"
              >
                <span>{{ worker.workerName }}</span>
                <button @click="removeWorkerFromSelection(worker.workerId)">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeModal">Скасувати</button>
          <button
            class="btn btn-primary"
            @click="handleAddWorkers"
            :disabled="selectedWorkers.length === 0"
          >
            Додати ({{ selectedWorkers.length }})
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { storeS } from "@/store";
import { mutateWorkerJob } from "@/usabilityScripts/globalMutate.js";

export default {
  name: "SelectWorkerModal",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Вибрати працівників",
    },
    alreadyAddedWorkerIds: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "add-workers"],
  data() {
    return {
      searchQuery: "",
      selectedWorkers: [],
      filteredWorkersList: [],
    };
  },
  computed: {
    allWorkers() {
      // Фільтруємо лише активних працівників (не заблокованих)
      const workers = storeS.workers || [];
      return workers.filter((worker) => {
        // Виключаємо заблокованих працівників
        return !worker.blocked && worker.statusShift !== "blocked";
      });
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.filterWorkers();
      } else {
        this.resetModal();
      }
    },
  },
  methods: {
    mutateWorkerJob,
    filterWorkers() {
      const query = this.searchQuery.toLowerCase().trim();

      if (!query) {
        this.filteredWorkersList = this.allWorkers;
        return;
      }

      this.filteredWorkersList = this.allWorkers.filter((worker) => {
        const name = (worker.workerName || "").toLowerCase();
        const job = (worker.workerJob || "").toLowerCase();
        const jobTitle = mutateWorkerJob(worker.workerJob).toLowerCase();

        return (
          name.includes(query) ||
          job.includes(query) ||
          jobTitle.includes(query)
        );
      });
    },
    isWorkerSelected(workerId) {
      return this.selectedWorkers.some((w) => w.workerId === workerId);
    },
    isWorkerAlreadyAdded(workerId) {
      return this.alreadyAddedWorkerIds.includes(workerId);
    },
    toggleWorkerSelection(worker) {
      if (this.isWorkerAlreadyAdded(worker.workerId)) {
        return;
      }

      const index = this.selectedWorkers.findIndex(
        (w) => w.workerId === worker.workerId
      );

      if (index > -1) {
        this.selectedWorkers.splice(index, 1);
      } else {
        this.selectedWorkers.push(worker);
      }
    },
    removeWorkerFromSelection(workerId) {
      const index = this.selectedWorkers.findIndex(
        (w) => w.workerId === workerId
      );
      if (index > -1) {
        this.selectedWorkers.splice(index, 1);
      }
    },
    clearAllSelections() {
      this.selectedWorkers = [];
    },
    getAvatarClass(worker) {
      return worker.statusShift === "open" ? "avatar-online" : "avatar-offline";
    },
    getStatusClass(worker) {
      return worker.statusShift === "open" ? "status-online" : "status-offline";
    },
    handleAddWorkers() {
      this.$emit("add-workers", this.selectedWorkers);
      this.closeModal();
    },
    closeModal() {
      this.$emit("close");
    },
    resetModal() {
      this.searchQuery = "";
      this.selectedWorkers = [];
      this.filteredWorkersList = [];
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #eff2f7;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-header-content i {
  font-size: 24px;
  color: #667eea;
}

.modal-header-content h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #495057;
}

.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #f8f9fa;
}

.modal-close i {
  font-size: 20px;
  color: #74788d;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.search-input-wrapper {
  position: relative;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.search-input-wrapper > i {
  position: absolute;
  left: 12px;
  font-size: 20px;
  color: #adb5bd;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 12px 40px 12px 44px;
  border: 2px solid #eff2f7;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  background: #f8f9fa;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.search-input::placeholder {
  color: #adb5bd;
}

.clear-search-btn {
  position: absolute;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  background: #e9ecef;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.clear-search-btn:hover {
  background: #d6dae0;
  transform: scale(1.05);
}

.clear-search-btn i {
  font-size: 18px;
  color: #74788d;
}

.workers-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #eff2f7;
  border-radius: 8px;
  background: #fff;
}

.worker-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #f8f9fa;
  cursor: pointer;
  transition: all 0.2s ease;
}

.worker-item:last-child {
  border-bottom: none;
}

.worker-item:hover:not(.disabled) {
  background: #f8f9fa;
}

.worker-item.selected {
  background: #f0f7ff;
  border-left: 3px solid #667eea;
}

.worker-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f8f9fa;
}

.worker-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #e9ecef;
  position: relative;
}

.worker-avatar.avatar-online::after {
  content: "";
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: #34c38f;
  border: 2px solid white;
  border-radius: 50%;
}

.worker-avatar.avatar-offline::after {
  content: "";
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: #adb5bd;
  border: 2px solid white;
  border-radius: 50%;
}

.avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.worker-avatar i {
  font-size: 24px;
  color: #74788d;
}

.worker-info {
  flex: 1;
  min-width: 0;
}

.worker-name {
  font-size: 14px;
  font-weight: 600;
  color: #212529;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.already-added-badge {
  display: inline-flex;
  padding: 2px 8px;
  background: #fef5f5;
  color: #f46a6a;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.worker-details {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
}

.worker-job {
  color: #667eea;
  font-weight: 500;
}

.worker-status {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #6c757d;
}

.worker-status.status-online {
  color: #34c38f;
}

.worker-status.status-offline {
  color: #adb5bd;
}

.worker-status i {
  font-size: 8px;
}

.worker-actions {
  flex-shrink: 0;
}

.worker-selected-icon {
  font-size: 24px;
  color: #34c38f;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #adb5bd;
}

.empty-state i {
  font-size: 48px;
  color: #dee2e6;
  margin-bottom: 12px;
  display: block;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

.selected-workers-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eff2f7;
}

.selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.selected-header span {
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.clear-all-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid #eff2f7;
  border-radius: 6px;
  font-size: 12px;
  color: #74788d;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-all-btn:hover {
  background: #fef5f5;
  border-color: #f46a6a;
  color: #f46a6a;
}

.selected-workers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-worker-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #667eea;
  color: white;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.selected-worker-chip button {
  width: 18px;
  height: 18px;
  border: none;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.selected-worker-chip button:hover {
  background: rgba(255, 255, 255, 0.5);
}

.selected-worker-chip button i {
  font-size: 12px;
  color: white;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #eff2f7;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-shrink: 0;
}

.btn {
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel {
  background: #fff;
  color: #74788d;
  border: 1px solid #eff2f7;
}

.btn-cancel:hover {
  background: #f8f9fa;
  border-color: #d6dae0;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #556ee6;
}

.btn-primary:disabled {
  background: #adb5bd;
  cursor: not-allowed;
  opacity: 0.6;
}

@media (max-width: 768px) {
  .modal-container {
    max-width: 100%;
  }

  .worker-item {
    padding: 10px;
  }

  .worker-avatar {
    width: 40px;
    height: 40px;
  }

  .worker-name {
    font-size: 13px;
  }

  .worker-details {
    font-size: 11px;
  }
}
</style>
